import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, PageBody, Section } from "../components/layout/Page";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
export default function Settings() {
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [newDes, setNewDes] = useState("");
  const [newHol, setNewHol] = useState({ name: "", date: "" });
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "hr_admin", password: "", company_id: activeCompanyId, assigned_companies: [] });
  const [newShift, setNewShift] = useState({ name: "", start_time: "09:30", end_time: "18:30", grace_minutes: 10, work_minutes: 480, is_default: false });
  const [pwdReset, setPwdReset] = useState({ user_id: "", password: "" });

  const canEdit = ["super_admin", "hr_admin"].includes(user?.role);
  const canManageUsers = ["super_admin", "hr_admin"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";

  const load = async () => {
    if (!activeCompanyId) return;
    const [d, des, h, sh] = await Promise.all([
      api.get(`/companies/${activeCompanyId}/departments`),
      api.get(`/companies/${activeCompanyId}/designations`),
      api.get("/settings/holidays", { params: { company_id: activeCompanyId } }),
      api.get("/shifts", { params: { company_id: activeCompanyId } }),
    ]);
    setDepartments(d.data); setDesignations(des.data); setHolidays(h.data); setShifts(sh.data);
    if (canManageUsers) {
      const u = await api.get("/users");
      setUsers(u.data);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const addDept = async () => {
    if (!newDept.trim()) return;
    await api.post(`/companies/${activeCompanyId}/departments`, { name: newDept });
    setNewDept(""); load(); toast.success("Department added");
  };
  const delDept = async (id) => { await api.delete(`/companies/${activeCompanyId}/departments/${id}`); load(); };

  const addDes = async () => {
    if (!newDes.trim()) return;
    await api.post(`/companies/${activeCompanyId}/designations`, { name: newDes });
    setNewDes(""); load(); toast.success("Designation added");
  };
  const delDes = async (id) => { await api.delete(`/companies/${activeCompanyId}/designations/${id}`); load(); };

  const addHol = async () => {
    if (!newHol.name || !newHol.date) return;
    await api.post("/settings/holidays", { ...newHol, company_id: activeCompanyId });
    setNewHol({ name: "", date: "" }); load(); toast.success("Holiday added");
  };
  const delHol = async (id) => { await api.delete(`/settings/holidays/${id}`); load(); };

  const addUser = async () => {
    try {
      await api.post("/users", { ...newUser, company_id: newUser.company_id || activeCompanyId, assigned_companies: [newUser.company_id || activeCompanyId] });
      toast.success("User created");
      setNewUser({ email: "", name: "", role: "hr_admin", password: "", company_id: activeCompanyId, assigned_companies: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteUser = async (u) => {
    if (u.id === user?.id) {
      toast.error("You cannot delete your own account");
      return;
    }
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete user");
    }
  };

  const addShift = async () => {
    if (!newShift.name.trim()) {
      toast.error("Shift name is required");
      return;
    }
    try {
      await api.post("/shifts", { ...newShift, company_id: activeCompanyId });
      toast.success("Shift created");
      setNewShift({ name: "", start_time: "09:30", end_time: "18:30", grace_minutes: 10, work_minutes: 480, is_default: false });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const delShift = async (id) => {
    if (!window.confirm("Delete shift? Employees assigned will be unassigned.")) return;
    await api.delete(`/shifts/${id}`);
    load();
  };

  const resetPassword = async () => {
    if (!pwdReset.user_id || pwdReset.password.length < 6) {
      toast.error("Pick a user and enter a password (min 6 chars)");
      return;
    }
    try {
      await api.post(`/users/${pwdReset.user_id}/reset-password`, { password: pwdReset.password });
      toast.success("Password updated");
      setPwdReset({ user_id: "", password: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const accrueLeave = async () => {
    if (!window.confirm("Credit this month's leave accrual (1.0 Casual + 0.5 Sick) to every active employee?")) return;
    try {
      const { data } = await api.post("/leaves/accrue-monthly", {});
      toast.success(`Accrued for ${data.credited} employees · skipped ${data.skipped} (already credited)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow={activeCompany?.code} title="Settings" subtitle="Master data and access control." />
      <PageBody>
        <Tabs defaultValue="masters">
          <TabsList className="rounded-sm border border-border bg-white p-1">
            <TabsTrigger value="masters" className="rounded-sm text-xs uppercase tracking-wider" data-testid="tab-masters">Masters</TabsTrigger>
            <TabsTrigger value="shifts" className="rounded-sm text-xs uppercase tracking-wider" data-testid="tab-shifts">Shifts</TabsTrigger>
            <TabsTrigger value="holidays" className="rounded-sm text-xs uppercase tracking-wider" data-testid="tab-holidays">Holidays</TabsTrigger>
            {canManageUsers && <TabsTrigger value="users" className="rounded-sm text-xs uppercase tracking-wider" data-testid="tab-users">Users</TabsTrigger>}
            {canManageUsers && <TabsTrigger value="maintenance" className="rounded-sm text-xs uppercase tracking-wider" data-testid="tab-maintenance">Maintenance</TabsTrigger>}
          </TabsList>

          <TabsContent value="masters" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Section title="Departments">
                {canEdit && (
                  <div className="flex gap-2 mb-4">
                    <Input value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="New department" className="rounded-sm" data-testid="dept-input" />
                    <Button onClick={addDept} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="add-dept"><Plus size={14} /></Button>
                  </div>
                )}
                <ul className="divide-y divide-border -m-5 mt-0">
                  {departments.map((d) => (
                    <li key={d.id} className="px-5 py-2 flex items-center justify-between" data-testid={`dept-${d.name}`}>
                      <span className="text-sm">{d.name}</span>
                      {canEdit && <button onClick={() => delDept(d.id)} className="p-1 text-[#F54236] hover:bg-[#F54236]/10 rounded-sm"><Trash size={12} /></button>}
                    </li>
                  ))}
                </ul>
              </Section>

              <Section title="Designations">
                {canEdit && (
                  <div className="flex gap-2 mb-4">
                    <Input value={newDes} onChange={(e) => setNewDes(e.target.value)} placeholder="New designation" className="rounded-sm" data-testid="des-input" />
                    <Button onClick={addDes} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="add-des"><Plus size={14} /></Button>
                  </div>
                )}
                <ul className="divide-y divide-border -m-5 mt-0">
                  {designations.map((d) => (
                    <li key={d.id} className="px-5 py-2 flex items-center justify-between" data-testid={`des-${d.name}`}>
                      <span className="text-sm">{d.name}</span>
                      {canEdit && <button onClick={() => delDes(d.id)} className="p-1 text-[#F54236] hover:bg-[#F54236]/10 rounded-sm"><Trash size={12} /></button>}
                    </li>
                  ))}
                </ul>
              </Section>
            </div>
          </TabsContent>

          <TabsContent value="shifts" className="mt-6 space-y-6">
            <Section title="Create shift">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <div><Label className="text-xs uppercase">Name</Label><Input value={newShift.name} onChange={(e) => setNewShift({...newShift, name: e.target.value})} placeholder="e.g. Evening" className="rounded-sm" data-testid="shift-name" /></div>
                <div><Label className="text-xs uppercase">Start (HH:MM)</Label><Input value={newShift.start_time} onChange={(e) => setNewShift({...newShift, start_time: e.target.value})} placeholder="09:30" className="rounded-sm font-mono" data-testid="shift-start" /></div>
                <div><Label className="text-xs uppercase">End (HH:MM)</Label><Input value={newShift.end_time} onChange={(e) => setNewShift({...newShift, end_time: e.target.value})} placeholder="18:30" className="rounded-sm font-mono" data-testid="shift-end" /></div>
                <div><Label className="text-xs uppercase">Grace (min)</Label><Input type="number" value={newShift.grace_minutes} onChange={(e) => setNewShift({...newShift, grace_minutes: parseInt(e.target.value || "0")})} className="rounded-sm" data-testid="shift-grace" /></div>
                <div><Label className="text-xs uppercase">Work mins</Label><Input type="number" value={newShift.work_minutes} onChange={(e) => setNewShift({...newShift, work_minutes: parseInt(e.target.value || "480")})} className="rounded-sm" data-testid="shift-work" /></div>
                <div className="flex items-end">
                  <Button onClick={addShift} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="add-shift"><Plus size={14} className="mr-1.5" /> Add</Button>
                </div>
              </div>
              <div className="text-[11px] text-[#64748B] mt-2">Default work day = 480 min (8 hours). Late mark applies if check-in is after start + grace.</div>
            </Section>
            <Section title={`${shifts.length} shifts for ${activeCompany?.code || ""}`} dense>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead>
                  <TableHead>Grace</TableHead><TableHead>Work mins</TableHead><TableHead>Default</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {shifts.map((s) => (
                    <TableRow key={s.id} data-testid={`shift-${s.name}`}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="font-mono text-xs">{s.start_time}</TableCell>
                      <TableCell className="font-mono text-xs">{s.end_time}</TableCell>
                      <TableCell className="font-mono text-xs">{s.grace_minutes}</TableCell>
                      <TableCell className="font-mono text-xs">{s.work_minutes}</TableCell>
                      <TableCell>{s.is_default ? <span className="text-[10px] uppercase font-semibold text-[#12B886]">Default</span> : ""}</TableCell>
                      <TableCell className="text-right">{canEdit && <button onClick={() => delShift(s.id)} className="p-1 text-[#F54236] hover:bg-[#F54236]/10 rounded-sm"><Trash size={12} /></button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          </TabsContent>

          <TabsContent value="holidays" className="mt-6">
            <Section title="Holidays">
              {canEdit && (
                <div className="flex gap-2 mb-4 flex-wrap">
                  <Input value={newHol.name} onChange={(e) => setNewHol({...newHol, name: e.target.value})} placeholder="Holiday name" className="rounded-sm flex-1 min-w-[160px]" data-testid="hol-name" />
                  <Input type="date" value={newHol.date} onChange={(e) => setNewHol({...newHol, date: e.target.value})} className="rounded-sm w-44" data-testid="hol-date" />
                  <Button onClick={addHol} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="add-hol"><Plus size={14} className="mr-1.5" /> Add</Button>
                </div>
              )}
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Scope</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {holidays.map((h) => (
                    <TableRow key={h.id} data-testid={`hol-${h.id}`}>
                      <TableCell className="font-mono text-xs">{h.date}</TableCell>
                      <TableCell>{h.name}</TableCell>
                      <TableCell className="text-xs">{h.company_id ? "Company" : "Global"}</TableCell>
                      <TableCell className="text-right">{canEdit && <button onClick={() => delHol(h.id)} className="p-1 text-[#F54236] hover:bg-[#F54236]/10 rounded-sm"><Trash size={12} /></button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Section>
          </TabsContent>

          {canManageUsers && (
            <TabsContent value="users" className="mt-6 space-y-6">
              <Section title="Create user">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div><Label className="text-xs uppercase">Name</Label><Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} className="rounded-sm" data-testid="user-name" /></div>
                  <div><Label className="text-xs uppercase">Email</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} className="rounded-sm" data-testid="user-email" /></div>
                  <div><Label className="text-xs uppercase">Password</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} className="rounded-sm" data-testid="user-pwd" /></div>
                  <div><Label className="text-xs uppercase">Role</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                      <SelectTrigger className="rounded-sm" data-testid="user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {user?.role === "super_admin" && <SelectItem value="super_admin">Super Admin</SelectItem>}
                        <SelectItem value="hr_admin">HR Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={addUser} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="add-user"><Plus size={14} className="mr-1.5" /> Create</Button>
                  </div>
                </div>
              </Section>

              <Section title={`${users.length} users`} dense>
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} data-testid={`user-${u.email}`}>
                        <TableCell>{u.name}</TableCell>
                        <TableCell className="font-mono text-xs">{u.email}</TableCell>
                        <TableCell><span className="text-[10px] uppercase tracking-wider font-semibold text-[#2563EB]">{u.role}</span></TableCell>
                        <TableCell className="text-xs">{u.created_at?.slice(0, 10)}</TableCell>
                        <TableCell className="text-right space-x-2">
                          {u.id !== user?.id && (
                            <button
                              onClick={() => setPwdReset({ user_id: u.id, password: "" })}
                              className="text-[10px] uppercase tracking-wider text-[#2563EB] hover:underline"
                              data-testid={`reset-pwd-${u.email}`}
                              title="Reset password"
                            >
                              Reset PW
                            </button>
                          )}
                          {u.id !== user?.id && (isSuperAdmin || u.role !== "super_admin") && (
                            <button
                              onClick={() => deleteUser(u)}
                              className="p-1.5 hover:bg-[#F54236]/10 text-[#F54236] rounded-sm align-middle"
                              data-testid={`delete-user-${u.email}`}
                              title="Delete user"
                            >
                              <Trash size={14} />
                            </button>
                          )}
                          {u.id === user?.id && (
                            <span className="text-[10px] uppercase tracking-wider text-[#64748B]">You</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Section>
              {pwdReset.user_id && (
                <Section title="Reset password">
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="text-xs text-[#64748B]">For: <span className="font-mono">{users.find((u) => u.id === pwdReset.user_id)?.email}</span></div>
                    <Input type="password" placeholder="New password (min 6 chars)" value={pwdReset.password} onChange={(e) => setPwdReset({...pwdReset, password: e.target.value})} className="rounded-sm w-64" data-testid="reset-pwd-input" />
                    <Button onClick={resetPassword} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="reset-pwd-submit">Save new password</Button>
                    <Button onClick={() => setPwdReset({ user_id: "", password: "" })} variant="outline" className="rounded-sm">Cancel</Button>
                  </div>
                </Section>
              )}
            </TabsContent>
          )}

          {canManageUsers && (
            <TabsContent value="maintenance" className="mt-6 space-y-6">
              <Section title="Leave accrual (monthly)">
                <div className="flex items-center gap-3">
                  <Button onClick={accrueLeave} className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="run-accrual">Run monthly accrual</Button>
                  <div className="text-xs text-[#64748B]">Adds 1.0 Casual + 0.5 Sick to every active employee. Idempotent — same month cannot be credited twice.</div>
                </div>
              </Section>
              {isSuperAdmin && (
                <Section title="Backup / restore">
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={async () => {
                        try {
                          const res = await api.get("/admin/backup", { responseType: "blob" });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `hrms_backup_${new Date().toISOString().slice(0, 10)}.json`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                          toast.success("Backup downloaded");
                        } catch (e) {
                          toast.error("Backup failed");
                        }
                      }}
                      className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8]"
                      data-testid="backup-btn"
                    >Download backup (JSON)</Button>
                    <div className="text-xs text-[#64748B]">Full snapshot of all HRMS collections, excluding password hashes. Keep this file safe.</div>
                  </div>
                </Section>
              )}
              {isSuperAdmin && (
                <Section title="Danger zone — wipe transactional data">
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={async () => {
                        if (!window.confirm("This will wipe ALL attendance, payslips and performance reviews across every company. Continue?")) return;
                        try {
                          const { data } = await api.post("/admin/wipe-transactional");
                          toast.success(`Wiped ${Object.values(data.wiped).reduce((a,b)=>a+b,0)} records`);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Failed");
                        }
                      }}
                      className="rounded-sm bg-[#F54236] hover:bg-[#DC2626] text-white"
                      data-testid="wipe-transactional-btn"
                    >Wipe attendance / payslips / reviews</Button>
                    <div className="text-xs text-[#64748B]">Use after Phase rollout to start with clean transactional data.</div>
                  </div>
                </Section>
              )}
            </TabsContent>
          )}
        </Tabs>
      </PageBody>
    </div>
  );
}
